import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, FileSignature, Download, Eye, Calendar, Search, PlusCircle, Pencil, Loader2, ListChecks, Trash2, Link2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useVendorInvoices } from '../../hooks/useVendorInvoices';
import { useVendorProfile } from '../../services/vendorProfileService';
import { vendorInvoiceApi, useDeleteVendorInvoice } from '../../services/vendorInvoiceService';
import { downloadInvoicePdfFromInvoiceData } from '../../utils/vendorInvoicePdf';
import { useToastContext } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import {
  VendorPageHeader,
  VendorGuideCallout,
  VendorEmptyState,
} from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';
import ChannelBadge from '../../components/shared/ChannelBadge';

const invoiceStatusColor = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'paid':
      return 'bg-emerald-100 text-emerald-800';
    case 'sent':
    case 'pending':
      return 'bg-amber-100 text-amber-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'draft':
    case 'cancelled':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Channel-locked invoice list.
 * Internal → platform-client invoices · External → guest invoices.
 */
const VendorInvoices = ({ channel = 'external' }) => {
  const isExternal = channel === 'external';
  const createPath = isExternal
    ? '/vendor/invoices/create?channel=external'
    : '/vendor/invoices/create?channel=internal';
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { success, error: showErrorToast } = useToastContext();
  const { confirm } = useConfirm();
  const { data: profileResp } = useVendorProfile();
  const queryClient = useQueryClient();
  const deleteInvoice = useDeleteVendorInvoice();

  const {
    invoices,
    isLoading,
    error,
    pagination,
    formatAmount,
    formatDate,
  } = useVendorInvoices(currentPage, 15, {
    search: searchTerm,
    channel,
  });

  const handleDownload = useCallback(
    async (invoiceId) => {
      setDownloadingId(invoiceId);
      try {
        const res = await vendorInvoiceApi.getOne(invoiceId);
        const inv = res?.data;
        const vp = profileResp?.data ?? {};
        if (inv) await downloadInvoicePdfFromInvoiceData(inv, vp);
      } catch {
        showErrorToast('Download failed', 'Could not download invoice. Try again.');
      } finally {
        setDownloadingId(null);
      }
    },
    [profileResp, showErrorToast]
  );

  const handleDelete = useCallback(
    async (invoice) => {
      if (String(invoice?.status || '').toLowerCase() === 'paid') {
        showErrorToast('Cannot delete', 'Paid invoices cannot be deleted.');
        return;
      }
      if (invoice?.booking_id || invoice?.has_booking) {
        showErrorToast(
          'Cannot delete',
          'This invoice is linked to a booking. Open the booking to manage it.'
        );
        return;
      }
      const ok = await confirm({
        title: 'Delete invoice?',
        message: `Delete invoice ${invoice.invoice_number || ''}? This cannot be undone.`,
        confirmLabel: 'Delete',
        variant: 'danger',
      });
      if (!ok) return;
      setDeletingId(invoice.id);
      try {
        await deleteInvoice.mutateAsync(invoice.id);
        success(null, 'Invoice deleted.');
        queryClient.invalidateQueries({ queryKey: ['vendorInvoices'] });
      } catch (e) {
        showErrorToast(null, e?.response?.data?.message || 'Failed to delete invoice.');
      } finally {
        setDeletingId(null);
      }
    },
    [confirm, deleteInvoice, queryClient, showErrorToast, success]
  );

  const canEditInvoice = (inv) =>
    inv?.status && !['paid', 'cancelled'].includes(String(inv.status).toLowerCase());

  const canDeleteInvoice = (inv) =>
    String(inv?.status || '').toLowerCase() !== 'paid' &&
    !inv?.booking_id &&
    !inv?.has_booking;

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title={isExternal ? 'External invoices' : 'Internal invoices'}
        subtitle={
          isExternal
            ? 'Guest and off-platform clients — outside marketplace accounting.'
            : 'Platform-client invoices from marketplace relationships.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ChannelBadge channel={channel} />
            <Link
              to={createPath}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-ink px-4 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-ink/40 focus:ring-offset-2 sm:w-auto"
            >
              <PlusCircle className="h-4 w-4" />
              Custom invoice
            </Link>
          </div>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.invoices.title}
        steps={VENDOR_PAGE_GUIDES.invoices.steps}
        icon={ListChecks}
      />

      <p className="text-xs text-ink-tertiary">
        {isExternal ? (
          <>
            Need platform-client invoices?{' '}
            <Link to="/vendor/internal-invoices" className="font-medium text-ink underline-offset-2 hover:underline">
              Open Internal invoices
            </Link>
          </>
        ) : (
          <>
            Billing a guest / off-platform client?{' '}
            <Link to="/vendor/invoices" className="font-medium text-ink underline-offset-2 hover:underline">
              Open External invoices
            </Link>
          </>
        )}
      </p>

      <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
        <div className="space-y-3 border-b border-border px-4 py-3 sm:px-5">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-xl border border-border bg-surface py-1.5 !pl-10 pr-4 text-xs text-ink placeholder:text-ink-tertiary focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/40"
            />
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-ink" />
              <p className="text-sm text-ink-tertiary">Loading invoices…</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              Failed to load invoices. Please try again.
            </div>
          ) : invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="product-surface-muted p-3 transition-colors hover:border-ink sm:p-4"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-gray-400">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-ink">
                            Invoice #{invoice.invoice_number || invoice.id}
                          </p>
                          <ChannelBadge invoice={invoice} />
                        </div>
                        {(invoice.client_name || invoice.guest_name) && (
                          <p className="text-sm text-ink-tertiary">
                            {invoice.client_name || invoice.guest_name}
                          </p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-ink-secondary">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            {formatDate(invoice.invoice_date || invoice.created_at)}
                          </span>
                          {invoice.total_amount != null && (
                            <span className="font-semibold tabular-nums text-ink">
                              ${formatAmount(invoice.total_amount)}
                            </span>
                          )}
                          {invoice.status && (
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${invoiceStatusColor(invoice.status)}`}
                            >
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </span>
                          )}
                        </div>
                        {(invoice.booking_id ||
                          invoice.has_booking ||
                          invoice.has_contract ||
                          invoice.primary_contract_id) && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(invoice.booking_id || invoice.has_booking) && (
                              <Link
                                to={`/vendor/bookings/${invoice.booking_id || invoice.booking?.id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-800 hover:bg-teal-100"
                              >
                                <Link2 className="h-3 w-3" />
                                {invoice.booking_reference || invoice.booking?.booking_reference
                                  ? `#${invoice.booking_reference || invoice.booking.booking_reference}`
                                  : 'Linked booking'}
                              </Link>
                            )}
                            {(invoice.has_contract || invoice.primary_contract_id) && (
                              <Link
                                to={`/vendor/contracts/${invoice.primary_contract_id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 hover:bg-violet-100"
                              >
                                <FileSignature className="h-3 w-3" />
                                Linked contract
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:pl-12">
                      <Link
                        to={`/vendor/invoices/${invoice.id}`}
                        className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink transition-colors hover:bg-surface-muted sm:px-4"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden sm:inline">View</span>
                      </Link>
                      {canEditInvoice(invoice) && (
                        <Link
                          to={`/vendor/invoices/${invoice.id}/edit?channel=${channel}`}
                          className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink transition-colors hover:bg-surface-muted sm:px-4"
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownload(invoice.id)}
                        disabled={downloadingId === invoice.id}
                        className="inline-flex items-center gap-2 rounded-md bg-black px-3 py-2 text-sm text-white transition-colors hover:bg-gray-800 disabled:opacity-60 sm:px-4"
                      >
                        {downloadingId === invoice.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">Download</span>
                      </button>
                      {canDeleteInvoice(invoice) && (
                        <button
                          type="button"
                          onClick={() => handleDelete(invoice)}
                          disabled={deletingId === invoice.id}
                          className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-surface px-3 py-2 text-sm text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60 sm:px-4"
                          title="Delete invoice"
                        >
                          {deletingId === invoice.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <VendorEmptyState
              icon={FileText}
              title={searchTerm ? 'No invoices match this search' : 'No invoices yet'}
              description={
                searchTerm
                  ? 'Try a different search term.'
                  : isExternal
                    ? 'Create a guest invoice for a private client or walk-in.'
                    : 'Create an invoice for a platform client from a marketplace booking.'
              }
              tips={
                searchTerm
                  ? []
                  : [
                      'Add your business logo in Account → Business',
                      'Include discounts and tax when needed',
                      'Email or download a PDF after saving',
                    ]
              }
              actionLabel={searchTerm ? undefined : 'Create custom invoice'}
              actionTo={searchTerm ? undefined : createPath}
            />
          )}
        </div>

        {pagination && pagination.last_page > 1 && (
          <div className="flex flex-col items-center justify-between gap-4 border-t border-border px-4 py-3 sm:flex-row sm:px-5">
            <div className="text-sm text-ink-secondary">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={pagination.current_page === 1}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-gray-800 transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let page;
                  if (pagination.last_page <= 5) {
                    page = i + 1;
                  } else if (pagination.current_page <= 3) {
                    page = i + 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    page = pagination.last_page - 4 + i;
                  } else {
                    page = pagination.current_page - 2 + i;
                  }

                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                        pagination.current_page === page
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 bg-surface text-gray-800 hover:bg-surface-muted'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(pagination.last_page, prev + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-gray-800 transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const VendorInternalInvoices = () => <VendorInvoices channel="internal" />;
export const VendorExternalInvoices = () => <VendorInvoices channel="external" />;

export default VendorInvoices;
